import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { BellRing, RefreshCw, Mail } from "lucide-react";

const SRC_LABEL = { whatsapp: "WhatsApp", qr: "QR code", share: "Link copiato", home: "Home app", direct: "Diretto", admin: "Console" };
const fmtDt = (s) => (s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

function Kpi({ label, value, testid }) {
  return <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-2" data-testid={testid}><div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div><div className="font-heading font-bold text-lg text-amber-300">{value}</div></div>;
}

// Statistiche di un'iniziativa: visite landing per canale, aperture in app, CTA, condivisioni + promemoria scadenza
export default function InitiativeStats({ campaignId, publishHome }) {
  const [s, setS] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get(`/campaigns/${campaignId}/initiative-stats`).then((r) => setS(r.data)).catch(() => {});
    api.get(`/campaigns/${campaignId}/initiative-results`).then((r) => setRes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps
  const remind = async () => {
    if (!window.confirm("Inviare ora la push «Ultimo giorno domani» a tutti i clienti con notifiche attive?")) return;
    setBusy(true);
    try { const { data } = await api.post(`/campaigns/${campaignId}/sheet/reminder/run-now?force=true`); toast.success(`Promemoria inviato: ${data.pushed} push a ${data.customers} clienti`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const summary = async () => {
    if (!window.confirm("Inviare ora il riepilogo risultati agli amministratori (email + notifica)?")) return;
    setBusy(true);
    try { const { data } = await api.post(`/campaigns/${campaignId}/sheet/summary/run-now?force=true`); toast.success(`Riepilogo inviato: ${data.sent?.email || 0} email · ${data.sent?.push || 0} push`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (!s) return null;
  const t = s.totals;
  const srcTotal = Object.values(s.sources).reduce((a, b) => a + b, 0);
  const best = Object.entries(s.sources)[0];
  return (
    <div className="rounded-lg border border-zinc-800 p-3 space-y-3 text-sm" data-testid="initiative-stats">
      <div className="flex items-center justify-between"><span className="font-semibold">Statistiche iniziativa</span><button type="button" onClick={load} className="text-zinc-500 hover:text-zinc-300" title="Aggiorna" data-testid="initiative-stats-refresh"><RefreshCw className="h-4 w-4" /></button></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi label="Visite landing" value={t.visits} testid="initiative-stat-visits" />
        <Kpi label="Aperture in app" value={t.home_opens} testid="initiative-stat-opens" />
        <Kpi label="Click CTA" value={t.cta} testid="initiative-stat-cta" />
        <Kpi label="Condivisioni staff" value={t.shares} testid="initiative-stat-shares" />
      </div>
      {res && (
        <div data-testid="initiative-results">
          <div className="text-xs text-zinc-500 mb-1">Risultati in cassa (movimenti con questa campagna)</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Kpi label="Scontrini" value={res.receipts} testid="initiative-result-receipts" />
            <Kpi label="Punti erogati" value={res.points} testid="initiative-result-points" />
            <Kpi label="Incasso" value={`${Number(res.revenue).toLocaleString("it-IT", { minimumFractionDigits: 2 })} €`} testid="initiative-result-revenue" />
            <Kpi label="Clienti" value={res.customers} testid="initiative-result-customers" />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-zinc-950 border border-zinc-800 p-2 mt-2">
            <div className="text-xs" data-testid="initiative-summary-status">
              <span className="font-semibold">Riepilogo fine iniziativa</span>{" "}
              {res.summary_sent_at ? <span className="text-emerald-300">{res.summary_manual ? "inviato manualmente" : "inviato"} il {fmtDt(res.summary_sent_at)} · {res.summary?.sent?.email || 0} email{res.summary_manual ? " · il riepilogo automatico partirà comunque a fine iniziativa" : ""}</span> : <span className="text-zinc-500">email + notifica agli admin il giorno dopo la fine (preferenze in Notifiche admin)</span>}
            </div>
            <Button type="button" size="sm" variant="outline" className="border-zinc-700 text-xs" disabled={busy} onClick={summary} data-testid="initiative-summary-send"><Mail className="h-4 w-4 mr-1" /> {busy ? "Invio…" : "Invia ora"}</Button>
          </div>
        </div>
      )}
      <div>
        <div className="text-xs text-zinc-500 mb-1">Da dove arrivano le visite{best && srcTotal ? <> · canale migliore: <span className="text-emerald-300 font-semibold" data-testid="initiative-stats-best">{SRC_LABEL[best[0]] || best[0]}</span></> : null}</div>
        {srcTotal === 0 ? <div className="text-xs text-zinc-500" data-testid="initiative-stats-empty">Nessuna visita registrata finora: condividi il link via WhatsApp o stampa il QR.</div> : (
          <div className="space-y-1">
            {Object.entries(s.sources).map(([k, n]) => (
              <div key={k} className="flex items-center gap-2 text-xs" data-testid={`initiative-source-${k}`}>
                <span className="w-24 text-zinc-400">{SRC_LABEL[k] || k}</span>
                <div className="flex-1 h-2 rounded bg-zinc-800 overflow-hidden"><div className="h-full bg-sky-500" style={{ width: `${Math.round((n / srcTotal) * 100)}%` }} /></div>
                <span className="w-16 text-right text-zinc-300">{n} · {Math.round((n / srcTotal) * 100)}%</span>
              </div>
            ))}
          </div>
        )}
        <div className="text-[11px] text-zinc-500 mt-1">Condivisioni: WhatsApp {t.share_wa} · Link {t.share_copy} · QR {t.share_qr}</div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-zinc-950 border border-zinc-800 p-2">
        <div className="text-xs" data-testid="initiative-reminder-status">
          <span className="font-semibold">Promemoria «Ultimo giorno domani»</span>{" "}
          {s.reminder?.sent_at ? <span className="text-emerald-300">inviato il {fmtDt(s.reminder.sent_at)} · {s.reminder.pushed} push a {s.reminder.customers} clienti</span> : <span className="text-zinc-500">{publishHome ? "automatico il giorno prima della scadenza (push ai clienti con notifiche attive)" : "attiva «Home dell'app cliente» per abilitarlo"}</span>}
        </div>
        <Button type="button" size="sm" variant="outline" className="border-zinc-700 text-xs" disabled={busy || !publishHome} onClick={remind} data-testid="initiative-reminder-send"><BellRing className="h-4 w-4 mr-1" /> {busy ? "Invio…" : "Invia ora"}</Button>
      </div>
    </div>
  );
}
